import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/** Hard stop on paging. The largest orgs in a group hold well over 2,000 open
 * Snyk Code issues, so an unbounded loop would keep a browser fetching for
 * minutes. When this trips the caller should say so rather than quietly
 * showing a subset - a silently truncated total is worse than an honest
 * partial one. */
export const MAX_PAGES = 60;

/** Snyk returns `links.next` either absolute or as a path. */
function absolute(next: string): string {
  return next.startsWith('http') ? next : `https://api.snyk.io${next}`;
}

/**
 * Rewrites a Snyk API URL onto the Backstage proxy.
 *
 * Calls must go through the proxy rather than direct to api.snyk.io: the token
 * lives in the backend (`SNYK_TOKEN`, injected into the proxy's Authorization
 * header) and must never reach the browser.
 */
export function viaProxy(proxyBaseUrl: string, snykUrl: string): string {
  const url = new URL(snykUrl);
  return `${proxyBaseUrl}/snyk${url.pathname}${url.search}`;
}

export type SnykPagesDeps = { fetchApi: FetchApi; discoveryApi: DiscoveryApi };

export type SnykPagesOptions = {
  /** Non-OK response on 0-based `page`: return an Error to throw it, or 'stop' to end paging quietly. */
  onNotOk: (status: number, page: number) => Error | 'stop';
  /** Checked after every fetch, before the ok check. */
  isStale?: () => boolean;
  /** Also check isStale between res.json() and handlePage. Required, not defaulted, so each call site shows its choice. */
  staleAfterJson: boolean;
};

/**
 * Pages through a Snyk list endpoint via the proxy, following `links.next` up
 * to MAX_PAGES. `truncated` is true when paging stopped before Snyk ran out of
 * pages, including a stale or 'stop' exit.
 */
export async function fetchSnykPages<T>(
  snykUrl: string,
  { fetchApi, discoveryApi }: SnykPagesDeps,
  { onNotOk, isStale, staleAfterJson }: SnykPagesOptions,
  handlePage: (data: T[]) => void,
): Promise<{ truncated: boolean }> {
  const proxyBaseUrl = await discoveryApi.getBaseUrl('proxy');
  let url = viaProxy(proxyBaseUrl, snykUrl);

  for (let page = 0; page < MAX_PAGES && url; page++) {
    const res = await fetchApi.fetch(url);
    if (isStale?.()) return { truncated: true };
    if (!res.ok) {
      const failure = onNotOk(res.status, page);
      if (failure === 'stop') return { truncated: true };
      throw failure;
    }
    const body = await res.json();
    if (staleAfterJson && isStale?.()) return { truncated: true };
    handlePage(body.data ?? []);

    const next = body.links?.next;
    url = next ? viaProxy(proxyBaseUrl, absolute(next)) : '';
  }

  return { truncated: Boolean(url) };
}
